import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '@/types/shared/auth';
import { useAuthStore } from '@/store/authStore';

// Consolidated Auth Types (merged from AuthTypes.ts)
export interface ToolboxUser {
  citizenId: string;
  toolboxType: string;
  fullId: string; // e.g., "Toolbox Type Citizen-12345"
  displayName: string;
  email: string;
  unit?: string;
  communityId: string;
  communityName?: string; // Added for display purposes
  permissions: string[];
  isActive: boolean;
  lastLoginAt: string;
  // Added for role-based navigation support
  role: User['role'];
  entityType?: string; // Added for proper role detection
}

export interface AuthContextType {
  user: ToolboxUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  updateUser: (updates: Partial<ToolboxUser>) => void;
  hasRole: (role: ToolboxUser['role'] | ToolboxUser['role'][]) => boolean;
  switchRole: (role: ToolboxUser['role']) => void; // For testing different entities
}

export interface LoginCredentials {
  email: string;
  password: string;
  communityId: string;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Base mock user data for demonstration (role will be injected at runtime)
const baseMockUser: ToolboxUser = {
  citizenId: '12345',
  toolboxType: 'Premium',
  fullId: 'Toolbox Type Citizen-12345',
  displayName: 'John Mitchell',
  email: 'john.mitchell@email.com',
  unit: '4A',
  communityId: 'tenant-glastonbury',
  permissions: ['payment.make', 'invoice.view', 'maintenance.view', 'governance.vote'],
  isActive: true,
  lastLoginAt: new Date().toISOString(),
  role: 'resident',
};

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<ToolboxUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Rotate storage key to avoid rehydrating stale super_admin sessions
  const STORAGE_KEY = 'toolbox_user_v2';

  useEffect(() => {
    // Simulate checking for existing session
    const checkAuthStatus = async () => {
      try {
        // In a real app, this would check for stored tokens/session
        const storedUser = localStorage.getItem(STORAGE_KEY);
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        } else {
          // For demo purposes, mirror Zustand auth role if available; default to resident
          const zustandRole = useAuthStore.getState().user?.role ?? 'resident';
          const initialUser: ToolboxUser = { ...baseMockUser, role: zustandRole };
          setUser(initialUser);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(initialUser));
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    setIsLoading(true);
    try {
      // Simulate API call to authenticate through Citizens App
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // In real implementation, this would verify credentials with Citizens App
      const authenticatedUser: ToolboxUser = {
        ...baseMockUser,
        email: credentials.email,
        communityId: credentials.communityId,
        lastLoginAt: new Date().toISOString(),
      };

      setUser(authenticatedUser);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(authenticatedUser));
    } catch (error) {
      throw new Error('Authentication failed', { cause: error });
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const updateUser = (updates: Partial<ToolboxUser>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedUser));
    }
  };

  const hasRole = (role: ToolboxUser['role'] | ToolboxUser['role'][]) => {
    if (!user) return false;

    if (Array.isArray(role)) {
      return role.includes(user.role);
    }

    return user.role === role;
  };

  // Dev-only. Role mutation is a privilege escalation and must never be callable
  // from a production bundle. See docs/superpowers/specs/2026-08-03-terminal-j-auth-integrity.md
  const switchRole = (role: ToolboxUser['role']) => {
    if (!import.meta.env.DEV) {
      console.warn('switchRole() is disabled outside development builds.');
      return;
    }
    if (user) {
      const updatedUser = { ...user, role };
      setUser(updatedUser);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedUser));
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUser,
    hasRole,
    switchRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
